import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  InvalidFormField,
  RegisteredAccountRequired,
  ShopNotClaimable,
  ValidationError,
} from "../errors";
import { parse } from "../model/common";
import { ClaimInvitationRequested, ClaimPreview } from "../model/shop";
import { Store } from "../model/store";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";

/**
 * `/shop-claims`: how the owner of an imported directory shop takes it over.
 * An invitation is emailed to the shop's saved contact address; its token is
 * previewed before sign-in and redeemed once by a registered account.
 * Delivery failures and an environment with claims switched off answer 503,
 * which the transport throws.
 */
export class ShopClaimsApi {
  constructor(private readonly t: Transport) {}

  /** Asks for an invitation to an imported, unclaimed shop. Registered accounts only; one delivery per shop a week. */
  async requestInvitation(
    ctx: ApiContext,
    storeId: string,
  ): Promise<
    Result<ClaimInvitationRequested, ApiNotAuthorized | RegisteredAccountRequired | ShopNotClaimable | InvalidFormField>
  > {
    const r = await this.t.post(ctx, "/shop-claims/request-invitation", { body: { storeId } });
    return forward(
      r,
      (j) => parse(ClaimInvitationRequested, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === "anonymous_not_allowed") return new RegisteredAccountRequired(e);
          if (e.incorrectValue === "") return new ShopNotClaimable(storeId, e.detailedDesc, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** What the invitation is for, without consuming it. A bad, used or expired token is `ApiNotAuthorized`. */
  async preview(ctx: ApiContext, token: string): Promise<Result<ClaimPreview, ApiNotAuthorized | InvalidFormField>> {
    const r = await this.t.post(ctx, "/shop-claims/preview", { body: { token } });
    return forward(
      r,
      (j) => parse(ClaimPreview, j),
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        return e;
      },
    );
  }

  /** Grants the invitation's shop to the caller. Once per token; registered accounts only. */
  async redeem(
    ctx: ApiContext,
    token: string,
  ): Promise<Result<Store, ApiNotAuthorized | RegisteredAccountRequired | InvalidFormField>> {
    const r = await this.t.post(ctx, "/shop-claims/redeem", { body: { token } });
    return forward(
      r,
      (j) => parse(Store, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === "anonymous_not_allowed") return new RegisteredAccountRequired(e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }
}
